<template>
  <aside class="menu-wrap select-none transition-all duration-300" :class="props.modelValue ? 'w-[312px]' : 'w-[92px]'">
    <div class="menu-panel h-full py-5" :class="props.modelValue ? 'px-4' : 'px-3'">
      <div class="menu-topbar border-b mb-3" :class="{ 'menu-topbar-collapsed': !props.modelValue }">
        <Flex center class="w-full">
          <router-link to="/home" :class="{ 'menu-brand-collapsed': !props.modelValue }">
            <img
              v-if="props.modelValue"
              src="@/assets/images/softwaresul_logo.png"
              alt="Softwaresul"
              class="menu-logo"
            />
            <span v-else class="menu-brand-mark">S</span>
          </router-link>
        </Flex>
      </div>

      <div class="menu-section-head" :class="{ 'menu-section-head-collapsed': !props.modelValue }">
        <div v-if="props.modelValue" class="menu-heading">Navegacao</div>
        <button
          type="button"
          class="menu-toggle"
          :title="props.modelValue ? 'Recolher menu' : 'Expandir menu'"
          @click="emits('update:modelValue', !props.modelValue)"
        >
          <i
            class="fa-regular fa-toggle-off text-[18px] transition-transform duration-300"
            :class="{ 'fa-toggle-on': props.modelValue }"
          ></i>
        </button>
      </div>

      <div v-for="(menuItem, index) in sortedMenuList" :key="index" class="menu-item-wrap mb-2">
        <a
          :href="getItemHref(menuItem)"
          :title="!props.modelValue ? menuItem.name : ''"
          @click="menuItemClicked($event, menuItem, sortedMenuList)"
          @mouseenter="setHoveredItem(menuItem)"
          @mouseleave="clearHoveredItem"
          class="menu-link"
          :class="[menuLinkClasses(menuItem, 0), { 'menu-link-collapsed': !props.modelValue }]"
        >
          <span class="menu-icon-shell">
            <i v-if="menuItem.icon" :class="menuItem.icon" class="menu-icon"></i>
          </span>
          <span class="menu-label" v-if="props.modelValue">{{ menuItem.name }}</span>
          <i
            v-if="menuItem.children && menuItem.children.length > 0 && props.modelValue"
            :class="`fa-solid fa-chevron-down transition ${menuItem.expanded ? 'rotate-180' : ''}`"
            class="menu-caret"
          ></i>
        </a>

        <div v-if="!props.modelValue && hoveredItemKey === getItemKey(menuItem)" class="menu-flyout">
          {{ menuItem.name }}
        </div>

        <div v-if="menuItem.expanded && props.modelValue" class="pl-4">
          <div v-for="(subItem, idx) in menuItem.children" :key="idx" class="menu-item-wrap mb-1.5">
            <a
              :href="getItemHref(subItem)"
              :title="subItem.name"
              @click="menuItemClicked($event, subItem, menuItem.children)"
              @mouseenter="setHoveredItem(subItem)"
              @mouseleave="clearHoveredItem"
              class="menu-link"
              :class="menuLinkClasses(subItem, 1)"
            >
              <span class="menu-icon-shell">
                <i v-if="subItem.icon" :class="subItem.icon" class="menu-icon"></i>
              </span>
              <span class="menu-label">{{ subItem.name }}</span>
              <i
                v-if="subItem.children && subItem.children.length > 0"
                :class="`fa-solid fa-chevron-down transition ${subItem.expanded ? 'rotate-180' : ''}`"
                class="menu-caret"
              ></i>
            </a>

            <div v-if="subItem.expanded" class="pl-4">
              <a
                v-for="(childItem, childIdx) in subItem.children"
                :key="childIdx"
                :href="getItemHref(childItem)"
                :title="childItem.name"
                @click="menuItemClicked($event, childItem, subItem.children)"
                @mouseenter="setHoveredItem(childItem)"
                @mouseleave="clearHoveredItem"
                class="menu-link"
                :class="menuLinkClasses(childItem, 2)"
              >
                <span class="menu-icon-shell">
                  <i v-if="childItem.icon" :class="childItem.icon" class="menu-icon"></i>
                </span>
                <span class="menu-label">{{ childItem.name }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup>
/* global defineProps, defineEmits */
import { get } from '@/crud'
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'

const route = useRoute()
const router = useRouter()
const store = useStore()

const props = defineProps({
  modelValue: Boolean,
})

const emits = defineEmits(['update:modelValue'])

const menuList = ref([])
const hoveredItemKey = ref(null)

const createSellerReceiptsMenuItem = () => ({
  id: 'seller-receipts',
  name: 'Recebimentos',
  screenRoute: '/recebimentos',
  icon: 'fa-solid fa-sack-dollar',
  expanded: false,
  children: [],
})

const canAccessRoute = (path, action = 'get') => {
  const permissions = store.state.user?.permissions?.byScreenRoute || {}
  return !!permissions?.[path]?.[action]
}

const sortedMenuList = computed(() => {
  const baseItems = Array.isArray(menuList.value) ? [...menuList.value] : []
  const sellerReceiptsMenuItem = createSellerReceiptsMenuItem()
  const alreadyExists = baseItems.some((item) => item?.screenRoute === sellerReceiptsMenuItem.screenRoute)

  if (!alreadyExists && canAccessRoute('/recebimentos')) {
    const dashboardIndex = baseItems.findIndex((item) => item?.screenRoute === '/dashboard-vendedora')
    if (dashboardIndex >= 0) {
      baseItems.splice(dashboardIndex + 1, 0, sellerReceiptsMenuItem)
    } else {
      baseItems.push(sellerReceiptsMenuItem)
    }
  }

  return baseItems
})

const fetchMenu = async () => {
  try {
    const response = await get('menu')
    menuList.value = response.data
  } catch (error) {
    console.error('Error fetching menu:', error)
  }
}

const hasChildren = (menuItem) => menuItem.children && menuItem.children.length > 0
const getItemKey = (menuItem) => menuItem?.id || menuItem?.screenRoute || menuItem?.name

const setHoveredItem = (menuItem) => {
  if (props.modelValue) return
  hoveredItemKey.value = getItemKey(menuItem)
}

const clearHoveredItem = () => {
  hoveredItemKey.value = null
}

const isNavigable = (menuItem) => menuItem.screenRoute && menuItem.screenRoute !== 'null'
const isLinkItem = (menuItem) => isNavigable(menuItem) && !hasChildren(menuItem)

const getItemHref = (menuItem) => {
  if (!isLinkItem(menuItem)) return null
  return router.resolve(menuItem.screenRoute).href
}

const resolvePath = (item) => {
  if (!isNavigable(item)) return null
  try {
    return router.resolve(item.screenRoute).path
  } catch (error) {
    return null
  }
}

const isActive = (item) => {
  const path = resolvePath(item)
  if (!path) return false
  return route.path === path || route.path.startsWith(`${path}/`)
}

const hasActiveDescendant = (item) => {
  if (!hasChildren(item)) return false
  return item.children.some((child) => isActive(child) || hasActiveDescendant(child))
}

const menuLinkClasses = (item, level = 0) => {
  const active = isActive(item) || hasActiveDescendant(item)
  const base = active ? 'menu-link-active' : 'menu-link-default'
  if (level === 0) return base
  if (level === 1) return `${base} menu-link-l2`
  return `${base} menu-link-l3`
}

const menuItemClicked = (event, menuItem, siblings) => {
  if (!event || event.button !== 0) return

  if (hasChildren(menuItem)) {
    event.preventDefault()
    siblings.forEach((item) => {
      if (item !== menuItem) {
        item.expanded = false
        closeAllChildren(item)
      }
    })
    emits('update:modelValue', true)
    menuItem.expanded = !menuItem.expanded
    return
  }

  if (!isNavigable(menuItem)) {
    event.preventDefault()
    return
  }

  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return

  event.preventDefault()
  goTo(menuItem)
}

const closeAllChildren = (menuItem) => {
  if (!menuItem.children) return
  menuItem.children.forEach((child) => {
    child.expanded = false
    if (child.children && child.children.length > 0) closeAllChildren(child)
  })
}

const goTo = (item) => {
  if (isNavigable(item)) router.push(item.screenRoute)
}

watch(
  route,
  async (newVal, oldVal) => {
    if (newVal.fullPath !== oldVal?.fullPath) {
      menuList.value = [...menuList.value]
    }
  },
  { deep: true },
)

onMounted(async () => {
  await fetchMenu()
})
</script>

<style scoped>
.menu-wrap {
  background: transparent;
  padding: 0;
}

.menu-panel {
  background: #ffffff;
  color: #3f4752;
  border-radius: 0;
  border-right: 1px solid #e5e7eb;
  box-shadow: none;
}

.menu-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 8px 14px;
}

.menu-topbar-collapsed {
  flex-direction: column;
  justify-content: center;
}


.menu-brand-collapsed {
  justify-content: center;
  width: 100%;
}

.menu-heading {
  padding-left: 10px;
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8b93a1;
  font-weight: 600;
}

.menu-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.menu-section-head-collapsed {
  justify-content: center;
  padding-bottom: 14px;
}

.menu-logo {
  width: 180px;
  max-width: 100%;
}

.menu-brand-mark {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eceff3;
  color: #151a22;
  font-size: 0.95rem;
  font-weight: 600;
}

.menu-toggle {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #66707f;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.menu-toggle:hover {
  background: #eef0f3;
  color: #1f2937;
}

.menu-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  border-radius: 16px;
  padding: 12px 14px;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 52px;
  width: 100%;
}

.menu-link-collapsed {
  justify-content: center;
  gap: 0;
  padding: 12px 0;
}

.menu-item-wrap {
  position: relative;
}

.menu-link-default {
  color: #66707f;
}

.menu-link-default:hover {
  background: #eef0f3;
  color: #1f2937;
}

.menu-link-active {
  background: #eceff3;
  color: #151a22;
  box-shadow: inset 0 0 0 1px #e1e5ea;
}

.menu-link-active:hover {
  background: #e8ebef;
}

.menu-link-l2,
.menu-link-l3 {
  font-size: 0.9rem;
}

.menu-icon-shell {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  flex: 0 0 28px;
  background: transparent;
  transition: inherit;
}

.menu-link-active .menu-icon-shell {
  background: #111111;
}

.menu-link-active .menu-icon {
  color: #ffffff;
}

.menu-icon {
  width: 18px;
  text-align: center;
  font-size: 1rem;
  color: inherit;
}

.menu-label {
  flex: 1;
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.2;
}

.menu-caret {
  font-size: 0.8rem;
  color: #8b93a1;
}

.menu-flyout {
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  border-radius: 12px;
  background: oklch(54.6% 0.245 262.881);
  color: #ffffff;
  padding: 8px 12px;
  font-size: 0.82rem;
  font-weight: 500;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18);
  z-index: 20;
}

.menu-flyout::before {
  content: '';
  position: absolute;
  left: -5px;
  top: 50%;
  width: 10px;
  height: 10px;
  background: oklch(54.6% 0.245 262.881);
  transform: translateY(-50%) rotate(45deg);
}

@media (max-width: 768px) {
  .menu-wrap {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 40;
  }
}
</style>
